import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Shield, LogOut, Lock, Trash2, AlertTriangle, Users, Clock,
  CheckCircle, CreditCard, DollarSign, Plane, Eye, XCircle,
  UserCheck, PlusCircle, RefreshCw, Search, MapPin, BarChart3
} from "lucide-react";
import getApiUrl from "@/utils/api";
import { AdminStatsCards } from "@/components/admin/AdminStatsCards";
import { BookingDetailsDialog } from "@/components/BookingDetailsDialog";
import { AnalyticsTab } from "@/components/admin/AnalyticsTab";
import { getAirlineColors, getAirlineCode } from "@/utils/airlineUtils";
import { AdminInstallPrompt } from "@/components/AdminInstallPrompt";

const API = getApiUrl();

export default function ITAdminDashboard() {
  const navigate = useNavigate();
  const [token, setToken] = useState(localStorage.getItem("it_admin_token"));
  const [admin, setAdmin] = useState(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: "", new_pw: "", confirm: "" });
  const [showPurgeConfirm, setShowPurgeConfirm] = useState(false);

  const getAuthHeaders = useCallback(() => ({
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [bookingsRes, statsRes] = await Promise.all([
        axios.get(`${API}/it-admin/bookings`, getAuthHeaders()),
        axios.get(`${API}/it-admin/stats`, getAuthHeaders())
      ]);
      const all = [
        ...(bookingsRes.data.individual_bookings || []).map(b => ({ ...b, booking_type: "individual" })),
        ...(bookingsRes.data.group_bookings || []).map(b => ({ ...b, booking_type: "group" }))
      ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setBookings(all);
      setStats(statsRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("it_admin_token");
        setToken(null);
        toast.error("Session expired");
      }
    } finally { setLoading(false); }
  }, [token, getAuthHeaders]);

  // Auto-update flight statuses via admin endpoint
  const autoUpdateFlights = useCallback(async () => {
    try {
      const adminToken = localStorage.getItem("adminToken");
      if (adminToken) {
        const res = await axios.post(`${API}/admin/flights/auto-update`, {}, { headers: { Authorization: `Bearer ${adminToken}` } });
        if (res.data.updated > 0) fetchData();
      }
    } catch (e) {}
  }, []);

  useEffect(() => { if (token) fetchData().then(() => autoUpdateFlights()); }, [token, fetchData, autoUpdateFlights]);

  // Auto-refresh when new bookings arrive (check every 30s)
  const [lastCount, setLastCount] = useState(0);
  useEffect(() => {
    if (!token) return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/it-admin/bookings/count`, getAuthHeaders());
        const newCount = res.data.total || 0;
        if (lastCount > 0 && newCount !== lastCount) {
          fetchData();
          toast.info("Dashboard refreshed — new booking activity");
        }
        setLastCount(newCount);
      } catch (e) {}
    }, 30000);
    return () => clearInterval(interval);
  }, [token, lastCount, getAuthHeaders, fetchData]);

  // Shared ride grouping
  const sharedRideGroups = useMemo(() => {
    const groups = {};
    bookings.forEach(b => {
      if (b.booking_status === 'cancelled') return;
      const date = (b.travel_date || '').split('T')[0];
      const airport = b.trip_type === 'arrival' ? (b.pickup_location || '') : (b.dropoff_location || '');
      const key = `${date}|${airport}|${b.trip_type}`;
      if (!groups[key]) groups[key] = { bookings: [], totalPax: 0, totalPrice: 0, totalPaid: 0, totalBalance: 0 };
      groups[key].bookings.push(b.id);
      groups[key].totalPax += (b.num_passengers || 1);
      groups[key].totalPrice += (b.trip_price || 0);
      groups[key].totalPaid += (b.amount_paid || 0);
      groups[key].totalBalance += Math.max(0, (b.trip_price || 0) - (b.amount_paid || 0));
    });
    const lookup = {};
    Object.values(groups).forEach(g => {
      if (g.totalPax >= 2) {
        g.bookings.forEach(id => { lookup[id] = g; });
      }
    });
    return lookup;
  }, [bookings]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    try {
      const res = await axios.post(`${API}/it-admin/login`, { email: loginForm.email, password: loginForm.password });
      localStorage.setItem("it_admin_token", res.data.token);
      setToken(res.data.token);
      setAdmin(res.data.admin);
      toast.success("IT Admin logged in");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally { setIsLoggingIn(false); }
  };

  const handleLogout = () => {
    localStorage.removeItem("it_admin_token");
    setToken(null);
    setAdmin(null);
  };

  const deleteBooking = async (bookingId) => {
    try {
      await axios.delete(`${API}/it-admin/bookings/${bookingId}`, getAuthHeaders());
      toast.success("Booking deleted permanently");
      setDeleteConfirmId(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    }
  };

  const purgeAllBookings = async () => {
    try {
      const res = await axios.delete(`${API}/it-admin/purge-test-bookings`, getAuthHeaders());
      toast.success(`Purged: ${res.data.deleted.bookings} bookings, ${res.data.deleted.group_bookings} groups`);
      setShowPurgeConfirm(false);
      fetchData();
    } catch (error) {
      toast.error("Purge failed");
    }
  };

  const changePassword = async () => {
    if (passwordForm.new_pw !== passwordForm.confirm) {
      toast.error("Passwords don't match");
      return;
    }
    try {
      await axios.post(`${API}/it-admin/change-password`, {
        current_password: passwordForm.current, new_password: passwordForm.new_pw
      }, getAuthHeaders());
      toast.success("Password changed");
      setShowChangePassword(false);
      setPasswordForm({ current: "", new_pw: "", confirm: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    }
  };

  const filteredBookings = bookings.filter(b => {
    const matchSearch = !searchTerm || 
      b.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      b.flight_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      b.id?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = statusFilter === "all" || b.booking_status === statusFilter;
    return matchSearch && matchStatus;
  });

  const getStatusBadge = (status) => {
    const map = {
      pending: "bg-yellow-100 text-yellow-700",
      confirmed: "bg-green-100 text-green-700",
      cancelled: "bg-red-100 text-red-700"
    };
    return <Badge className={map[status] || "bg-gray-100"}>{status}</Badge>;
  };

  // Login Screen
  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4" data-testid="it-admin-login">
        <Card className="w-full max-w-md border-0 shadow-2xl bg-slate-800/80 backdrop-blur-xl">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 rounded-2xl bg-red-600 flex items-center justify-center mb-4">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-white">IT Administrator</CardTitle>
            <p className="text-sm text-slate-400">Easy Transportation - System Access</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label className="text-slate-300">Username</Label>
                <Input
                  value={loginForm.email}
                  onChange={(e) => setLoginForm(p => ({ ...p, email: e.target.value }))}
                  className="bg-slate-700 border-slate-600 text-white"
                  data-testid="it-admin-username"
                />
              </div>
              <div>
                <Label className="text-slate-300">Password</Label>
                <Input
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm(p => ({ ...p, password: e.target.value }))}
                  className="bg-slate-700 border-slate-600 text-white"
                  data-testid="it-admin-password"
                />
              </div>
              <Button type="submit" className="w-full bg-red-600 hover:bg-red-700" disabled={isLoggingIn} data-testid="it-admin-login-btn">
                {isLoggingIn ? "Signing in..." : "Sign In"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="min-h-screen bg-slate-50" data-testid="it-admin-dashboard">
      <AdminInstallPrompt
        storageKey="itAdminInstallDismissed"
        title="Install IT Admin App"
        description="Install the IT Admin Dashboard for quick system access and management."
        headerBg="bg-gradient-to-r from-red-700 to-red-900"
      />
      {/* Header */}
      <header className="bg-slate-900 text-white px-3 md:px-4 py-2.5 md:py-3 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-2 md:gap-3 min-w-0">
          <div className="w-8 h-8 md:w-9 md:h-9 rounded-lg bg-red-600 flex items-center justify-center flex-shrink-0">
            <Shield className="w-4 h-4 md:w-5 md:h-5" />
          </div>
          <div className="min-w-0">
            <h1 className="font-bold text-base md:text-lg truncate">IT Administrator</h1>
            <p className="text-[10px] md:text-xs text-slate-400">System Management</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={fetchData} className="text-slate-300 hover:text-white" data-testid="it-refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setShowChangePassword(true)} className="text-slate-300 hover:text-white" data-testid="it-change-pw-btn">
            <Lock className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-red-400 hover:text-red-300" data-testid="it-logout-btn">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-3 md:p-4 space-y-4 md:space-y-6">
        {/* Stats */}
        {stats && <AdminStatsCards stats={stats} onStatClick={() => {}} />}

        {/* Danger Zone */}
        <Card className="border-red-200 bg-red-50/50">
          <CardContent className="p-3 md:p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-red-800 text-sm">Danger Zone</p>
                  <p className="text-xs text-red-600">Destructive actions - use with caution</p>
                </div>
              </div>
              <Dialog open={showPurgeConfirm} onOpenChange={setShowPurgeConfirm}>
                <DialogTrigger asChild>
                  <Button variant="destructive" size="sm" className="w-full sm:w-auto flex-shrink-0" data-testid="purge-all-btn">
                    <Trash2 className="w-4 h-4 mr-1" /> Purge All Bookings
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="text-red-600 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5" /> Confirm Purge
                    </DialogTitle>
                  </DialogHeader>
                  <p className="text-sm text-muted-foreground">This will permanently delete ALL bookings, group bookings, and payment transactions. This action cannot be undone.</p>
                  <div className="flex gap-2 justify-end mt-4">
                    <Button variant="outline" onClick={() => setShowPurgeConfirm(false)}>Cancel</Button>
                    <Button variant="destructive" onClick={purgeAllBookings} data-testid="purge-confirm-btn">
                      <Trash2 className="w-4 h-4 mr-1" /> Delete Everything
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="bookings">
          <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide">
            <TabsList className="bg-white border inline-flex w-max md:w-auto">
              <TabsTrigger value="bookings" className="rounded-lg data-[state=active]:bg-slate-100 text-xs md:text-sm">
                <Users className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1 md:mr-2" /> Bookings
              </TabsTrigger>
              <TabsTrigger value="analytics" className="rounded-lg data-[state=active]:bg-slate-100 text-xs md:text-sm">
                <BarChart3 className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1 md:mr-2" /> Analytics
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="bookings">
            {/* Bookings Table */}
            <Card className="border-0 shadow-lg">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between flex-wrap gap-2 md:gap-3">
                  <CardTitle className="text-base md:text-lg flex items-center gap-2">
                    <Users className="w-4 h-4 md:w-5 md:h-5" />
                    All Bookings ({filteredBookings.length})
                  </CardTitle>
                  <div className="flex items-center gap-2 w-full md:w-auto">
                    <div className="relative flex-1 md:flex-none">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9 h-9 w-full md:w-64"
                        data-testid="it-search-input"
                      />
                    </div>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="h-9 w-28 md:w-36" data-testid="it-status-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="confirmed">Confirmed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0 md:p-0">
                {/* Mobile Card View */}
                <div className="md:hidden p-3 space-y-3" data-testid="it-bookings-mobile-cards">
                  {filteredBookings.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No bookings found</p>
                  ) : filteredBookings.map((booking) => {
                    const flightNum = (booking.flight_number || '').toUpperCase();
                    const airlineCode = getAirlineCode(flightNum);
                    const colors = getAirlineColors(airlineCode);
                    return (
                      <div key={booking.id} className="border rounded-xl p-3 space-y-2 hover:shadow-md transition-shadow" data-testid={`it-mobile-booking-${booking.id}`}>
                        <div className="flex items-start justify-between">
                          <div className="min-w-0">
                            <p className="font-semibold text-sm truncate">{booking.full_name}</p>
                            <p className="text-[10px] text-muted-foreground">{booking.whatsapp_number}</p>
                          </div>
                          <div className="flex flex-col items-end gap-0.5">
                            {getStatusBadge(booking.booking_status)}
                            <Badge className={`text-[9px] px-1.5 py-0 ${booking.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}`}>{booking.payment_status}</Badge>
                          </div>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{booking.travel_date}</span>
                            <span className="text-muted-foreground">{booking.travel_time}</span>
                            {booking.trip_type === "return" && <Badge variant="secondary" className="text-[9px] px-1 py-0 bg-orange-100 text-orange-700">Return</Badge>}
                          </div>
                          {flightNum && (
                            <div className="flex items-center gap-1">
                              <span className={`inline-flex items-center justify-center w-5 h-5 rounded text-white font-bold text-[8px] ${colors.bg}`}>{airlineCode || '??'}</span>
                              <span className={`text-xs font-semibold ${colors.text}`}>{flightNum}</span>
                            </div>
                          )}
                        </div>
                        <p className="text-[11px] text-muted-foreground truncate">{booking.pickup_location} → {booking.dropoff_location || booking.housing_unit}</p>
                        <div className="flex items-center justify-between text-xs bg-gray-50 rounded-lg p-2">
                          <div><span className="font-bold">${(booking.trip_price || 0).toFixed(2)}</span> <span className="text-muted-foreground">total</span></div>
                          <div><span className="text-green-700 font-semibold">${(booking.amount_paid || 0).toFixed(2)}</span> <span className="text-muted-foreground">paid</span></div>
                          <div><span className={`font-bold ${(booking.balance_due || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>${(booking.balance_due || 0).toFixed(2)}</span> <span className="text-muted-foreground">due</span></div>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Button variant="outline" size="sm" onClick={() => setSelectedBooking(booking)} className="flex-1 h-8 text-[10px] gap-1" data-testid={`it-mobile-view-${booking.id}`}>
                            <Eye className="w-3 h-3" /> View
                          </Button>
                          {deleteConfirmId === booking.id ? (
                            <>
                              <Button variant="destructive" size="sm" onClick={() => deleteBooking(booking.id)} className="h-8 text-[10px] gap-1"><Trash2 className="w-3 h-3" /> Yes</Button>
                              <Button variant="ghost" size="sm" onClick={() => setDeleteConfirmId(null)} className="h-8 text-[10px]">No</Button>
                            </>
                          ) : (
                            <Button variant="outline" size="sm" onClick={() => setDeleteConfirmId(booking.id)} className="h-8 text-[10px] gap-1 text-red-500 border-red-200" data-testid={`it-mobile-delete-${booking.id}`}>
                              <Trash2 className="w-3 h-3" /> Delete
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="p-3 text-left font-medium">Passenger</th>
                        <th className="p-3 text-left font-medium">Date/Time</th>
                        <th className="p-3 text-left font-medium">Flight</th>
                        <th className="p-3 text-left font-medium">Route</th>
                        <th className="p-3 text-left font-medium">Amount</th>
                        <th className="p-3 text-left font-medium">Paid</th>
                        <th className="p-3 text-left font-medium">Status</th>
                        <th className="p-3 text-right font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredBookings.map((booking) => {
                        const flightNum = (booking.flight_number || '').toUpperCase();
                        const airlineCode = getAirlineCode(flightNum);
                        const colors = getAirlineColors(airlineCode);
                        const status = booking.flight_status;
                        return (
                          <tr key={booking.id} className="border-b hover:bg-slate-50/50" data-testid={`it-booking-row-${booking.id}`}>
                            <td className="p-3">
                              <p className="font-medium">{booking.full_name}</p>
                              <p className="text-xs text-muted-foreground">{booking.whatsapp_number}</p>
                              <p className="text-[10px] text-slate-400 font-mono">{booking.id?.slice(0, 8)}</p>
                            </td>
                            <td className="p-3">
                              <p className="font-medium">{booking.travel_date}</p>
                              <p className="text-xs text-muted-foreground">{booking.travel_time}</p>
                              {booking.trip_type === "return" && (
                                <Badge variant="secondary" className="mt-1 text-[10px] bg-orange-100 text-orange-700">Return</Badge>
                              )}
                            </td>
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg text-white font-bold text-xs ${colors.bg}`}>
                                  {airlineCode || '??'}
                                </span>
                                <div>
                                  <span className={`text-sm font-semibold ${colors.text}`}>{flightNum || 'N/A'}</span>
                                  <p className="text-xs text-muted-foreground">{booking.airline || 'Unknown'}</p>
                                  {status && (
                                    <Badge className={`mt-0.5 text-[10px] px-1.5 py-0 ${
                                      status === 'landed' ? 'bg-green-100 text-green-700' :
                                      status === 'active' ? 'bg-blue-100 text-blue-700' :
                                      status === 'incident' || status === 'diverted' ? 'bg-orange-100 text-orange-700' :
                                      status === 'cancelled' ? 'bg-red-200 text-red-800' :
                                      (status === 'delayed' || (booking.delay_minutes || 0) > 15) ? 'bg-red-100 text-red-700' :
                                      status === 'scheduled' ? 'bg-gray-100 text-gray-600' :
                                      'bg-gray-100 text-gray-600'
                                    }`}>
                                      <Plane className="w-2.5 h-2.5 mr-0.5" />
                                      {status === 'landed' ? 'Landed' :
                                       status === 'active' ? 'In Air' :
                                       status === 'delayed' ? 'Delayed' :
                                       status === 'scheduled' ? 'On Time' :
                                       status === 'cancelled' ? 'Cancelled' :
                                       status.charAt(0).toUpperCase() + status.slice(1)}
                                      {booking.landing_time ? ` ${booking.landing_time}` : ''}
                                    </Badge>
                                  )}
                                  {(booking.delay_minutes || 0) > 0 && (
                                    <p className="text-[10px] text-red-600 font-medium">+{booking.delay_minutes} min delay</p>
                                  )}
                                  {(booking.arrival_terminal || booking.arrival_gate || booking.arrival_baggage) && (
                                    <p className="text-[10px] text-muted-foreground mt-0.5">
                                      {[
                                        booking.arrival_terminal ? `T${booking.arrival_terminal}` : '',
                                        booking.arrival_gate ? `Gate ${booking.arrival_gate}` : '',
                                        booking.arrival_baggage ? `Bag ${booking.arrival_baggage}` : ''
                                      ].filter(Boolean).join(' | ')}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="p-3">
                              <div className="flex items-start gap-1">
                                <MapPin className="w-3 h-3 text-green-600 mt-0.5 flex-shrink-0" />
                                <span className="text-xs truncate max-w-[150px]">{booking.dropoff_location || booking.housing_unit || "-"}</span>
                              </div>
                              <p className="text-xs text-muted-foreground">{booking.num_passengers} pax</p>
                              {booking.custom_address && (
                                <p className="text-[10px] text-blue-600 truncate max-w-[150px]">{booking.custom_address}</p>
                              )}
                            </td>
                            <td className="p-3">
                              <p className="font-bold">${(booking.trip_price || 0).toFixed(2)}</p>
                              {booking.num_passengers > 1 && (
                                <p className="text-[10px] text-indigo-600">${(booking.per_person_price || (booking.trip_price / booking.num_passengers)).toFixed(2)}/person</p>
                              )}
                              <p className="text-xs text-muted-foreground">Dep: ${(booking.deposit_amount || 0).toFixed(2)}</p>
                              {sharedRideGroups[booking.id] && (
                                <div className="mt-1 bg-blue-50 border border-blue-200 rounded-lg px-2 py-1">
                                  <div className="flex items-center gap-1 text-[10px] text-blue-700 font-semibold">
                                    <Users className="w-3 h-3" />
                                    Shared ({sharedRideGroups[booking.id].totalPax} pax)
                                  </div>
                                  <p className="text-[10px] text-blue-600">Group: ${sharedRideGroups[booking.id].totalPrice.toFixed(2)}</p>
                                  <p className="text-[10px] font-bold text-red-600">Due: ${sharedRideGroups[booking.id].totalBalance.toFixed(2)}</p>
                                </div>
                              )}
                            </td>
                            <td className="p-3">
                              <p className="font-semibold text-green-700">${(booking.amount_paid || 0).toFixed(2)}</p>
                              {booking.num_passengers > 1 && (
                                <p className="text-[10px] text-green-600">${((booking.amount_paid || 0) / booking.num_passengers).toFixed(2)}/person</p>
                              )}
                              {booking.payment_method && (
                                <Badge variant="outline" className={`text-[10px] mt-0.5 ${
                                  booking.payment_method === 'cash' ? 'bg-green-50 text-green-700 border-green-200' :
                                  booking.payment_method === 'stripe' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                  booking.payment_method === 'zelle' ? 'bg-purple-50 text-purple-700 border-purple-200' :
                                  'bg-gray-50 text-gray-700 border-gray-200'
                                }`}>
                                  {booking.payment_method === 'cash' ? 'Cash' :
                                   booking.payment_method === 'stripe' ? 'Stripe' :
                                   booking.payment_method === 'zelle' ? 'Zelle' :
                                   booking.payment_method}
                                </Badge>
                              )}
                            </td>
                            <td className="p-3">{getStatusBadge(booking.booking_status)}</td>
                            <td className="p-3">
                              <div className="flex items-center justify-end gap-1">
                                <Button variant="ghost" size="sm" onClick={() => setSelectedBooking(booking)} data-testid={`it-view-${booking.id}`}>
                                  <Eye className="w-4 h-4" />
                                </Button>
                                {deleteConfirmId === booking.id ? (
                                  <div className="flex items-center gap-1">
                                    <Button variant="destructive" size="sm" onClick={() => deleteBooking(booking.id)} data-testid={`it-delete-confirm-${booking.id}`}>
                                      <Trash2 className="w-3 h-3 mr-1" /> Yes
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => setDeleteConfirmId(null)}>No</Button>
                                  </div>
                                ) : (
                                  <Button variant="ghost" size="sm" onClick={() => setDeleteConfirmId(booking.id)}
                                    className="text-red-400 hover:text-red-600 hover:bg-red-50"
                                    data-testid={`it-delete-btn-${booking.id}`} title="Delete Booking">
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {filteredBookings.length === 0 && (
                        <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">No bookings found</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="analytics">
            <AnalyticsTab bookings={bookings} />
          </TabsContent>
        </Tabs>
      </main>

      {/* Change Password Dialog */}
      <Dialog open={showChangePassword} onOpenChange={setShowChangePassword}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Lock className="w-5 h-5" /> Change IT Admin Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Current Password</Label>
              <Input type="password" value={passwordForm.current} onChange={(e) => setPasswordForm(p => ({ ...p, current: e.target.value }))} className="h-9" />
            </div>
            <div>
              <Label className="text-xs">New Password</Label>
              <Input type="password" value={passwordForm.new_pw} onChange={(e) => setPasswordForm(p => ({ ...p, new_pw: e.target.value }))} className="h-9" />
            </div>
            <div>
              <Label className="text-xs">Confirm New Password</Label>
              <Input type="password" value={passwordForm.confirm} onChange={(e) => setPasswordForm(p => ({ ...p, confirm: e.target.value }))} className="h-9" />
            </div>
            <Button onClick={changePassword} className="w-full" data-testid="it-change-pw-submit">Change Password</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Booking Details Dialog */}
      {selectedBooking && (
        <Dialog open={!!selectedBooking} onOpenChange={(open) => { if (!open) setSelectedBooking(null); }}>
          <BookingDetailsDialog
            selectedBooking={selectedBooking}
            drivers={[]}
            getAuthHeaders={() => ({ headers: { Authorization: `Bearer ${localStorage.getItem("it_admin_token")}` } })}
            fetchData={fetchData}
            onClose={() => setSelectedBooking(null)}
            editApiPrefix="/it-admin"
          />
        </Dialog>
      )}
    </div>
  );
}
